import React, { useEffect, useState } from "react";
import {
  Box,
  CssBaseline,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  ButtonGroup,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axiosInstance"
import AppBarComponent from "../../components/layout/AppbarComponent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useConfirm } from "../../utils/useConfirm";

const processData = [
  {
    key: "plan_ratio",
    process: "การวางแผน",
    role: "ผู้บริหารโครงการ",
    coreprocess: true,
  },
  {
    key: "req_ratio",
    process: "การวิเคราะห์ความต้องการ",
    role: "นักวิเคราะห์และออกแบบระบบงาน",
    coreprocess: true,
  },
  {
    key: "design_ratio",
    process: "การออกแบบซอฟต์แวร์",
    role: "นักวิเคราะห์และออกแบบระบบงาน",
    coreprocess: true,
  },
  {
    key: "dev_ratio",
    process: "การพัฒนาซอฟต์แวร์",
    role: "นักพัฒนาซอฟต์แวร์",
    coreprocess: true,
  },
  {
    key: "test_ratio",
    process: "การทดสอบซอฟต์แวร์",
    role: "นักทดสอบซอฟต์แวร์",
    coreprocess: true,
  },
  {
    key: "deliver_ratio",
    process: "การส่งมอบ",
    role: "ฝ่ายปฏิบัติการสนับสนุน",
    coreprocess: true,
  },
  {
    key: "risk_ratio",
    process: "ความเสี่ยง",
    role: "ผู้เกี่ยวข้อง",
    coreprocess: false,
  },
  {
    key: "tech_ratio",
    process: "เทคโนโลยีที่คิดค้นขึ้นใหม่",
    role: "ผู้เกี่ยวข้อง",
    coreprocess: false,
  },
  {
    key: "other_ratio",
    process: "ปัจจัยอื่น ๆ",
    role: "ผู้เกี่ยวข้อง",
    coreprocess: false,
  },
];

const EditSoftware = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const API_URL = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({});
  const [fetchData, setFetchData] = useState({});
  const [parameters, setParameters] = useState({});
  const [loading, setLoading] = useState(true);

  const safeParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const userData = safeParse(localStorage.getItem("user"));
const estimator = userData?.user_name || "";

  // Fetch software data
  useEffect(() => {
    const fetchSoftware = async () => {
      try {
        const res = await api.get(`api/software/getsoftware/${id}`);
        setFetchData(res.data);
        setFormData(res.data);
      } catch (err) {
        console.error("Error fetching software details", err);
      }
    };
    fetchSoftware();
  }, [id, API_URL]);

  // Fetch latest parameters
  useEffect(() => {
    const fetchParameters = async () => {
      try {
        const res = await api.get(`api/parameter/latest`);
        setParameters(res.data);
      } catch (err) {
        console.error("Error fetching parameters", err);
      } finally {
        setLoading(false);
      }
    };
    fetchParameters();
  }, [API_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericValue = Number(value);

    if (numericValue < 0) {
      setFormData((prev) => ({ ...prev, [name]: 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    if (
      processData
        .filter((proc) => proc.coreprocess)
        .reduce((sum, proc) => sum + Number(formData[proc.key] || 0), 0) !== 100
    ) {
      toast.error("อัตราส่วนของกระบวนการหลักต้องรวมกันเป็น 100%");
      return;
    }
    try {
      const payload = { ...formData, estimator, software_id: id }; // include software_id in body
      await api.put(`api/software/editsoftware`, payload);
      toast.success("บันทึกข้อมูลเรียบร้อยแล้ว!", {
        autoClose: 1500,
      });
      setFetchData(payload);
    } catch (err) {
      console.error("Error saving software:", err);
      toast.error("บันทึกไม่สำเร็จ");
    }
  };

  const handleNext = async () => {
    const hasChanges = JSON.stringify(fetchData) !== JSON.stringify(formData);

    if (hasChanges) {
      const ok = await confirm(
        "คุณยังไม่ได้บันทึกการเปลี่ยนแปลง ต้องการดำเนินการต่อหรือไม่?"
      );
      if (!ok) return;
    }

    navigate(`/software/addusecase/${id}`);
  };

  const handleBack = async () => {
    const hasChanges = JSON.stringify(fetchData) !== JSON.stringify(formData);

    if (hasChanges) {
      const ok = await confirm(
        "คุณยังไม่ได้บันทึกการเปลี่ยนแปลง ต้องการดำเนินการต่อหรือไม่?"
      );
      if (!ok) return;
    }
    navigate("/estimate");
  };

  if (loading) return <Typography>กำลังโหลดข้อมูล...</Typography>;

  return (
    <>
      <CssBaseline />
      <AppBarComponent />
      <ToastContainer position="top-center" autoClose={2000} />
      <Box sx={{ p: 2, backgroundColor: "#DFF6F0", minHeight: "100vh" }}>
        <Paper
          sx={{
            width: "95%",
            margin: "0 auto",
            p: 3,
            borderRadius: 2.5,
            backgroundColor: "#E6F7F4",
          }}
        >
          <Typography variant="h5" mb={2} sx={{ fontWeight: "bold" }}>
            รหัสโครงการ : {id}{" "}
            {formData.software_name
              ? `- ชื่อโครงการ : ${formData.software_name}`
              : ""}
          </Typography>
          <Typography variant="h6" mb={2} sx={{ fontWeight: "bold" }}>
            กำหนดค่าเริ่มต้น
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    กระบวนการทำงาน
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>อัตราส่วน</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    อัตราส่วนที่แนะนำ
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    บทบาทหน้าที่หลัก
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    อัตราค่าจ้างต่อเดือน (บาท)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processData
                  .filter((proc) => proc.coreprocess)
                  .map((proc) => {
                    let salaryKey = "";
                    if (proc.role.includes("โครงการ")) salaryKey = "pm_salary";
                    else if (
                      proc.role.includes("วิเคราะห์") ||
                      proc.role.includes("ออกแบบ")
                    )
                      salaryKey = "sa_salary";
                    else if (proc.role.includes("นักพัฒนาซอฟต์แวร์"))
                      salaryKey = "dev_salary";
                    else if (proc.role.includes("นักทดสอบ"))
                      salaryKey = "tester_salary";
                    else if (proc.role.includes("ฝ่ายปฏิบัติการ"))
                      salaryKey = "workteam_salary";
                    else salaryKey = "support_salary";

                    return (
                      <TableRow key={proc.key}>
                        <TableCell>{proc.process}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            name={proc.key}
                            value={formData[proc.key] || ""}
                            onChange={handleChange}
                            slotProps={{ input: { min: 0 } }}
                          />
                        </TableCell>
                        <TableCell>{parameters[proc.key] ?? "-"}</TableCell>
                        <TableCell>{proc.role}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            name={salaryKey}
                            value={formData[salaryKey] || ""}
                            onChange={handleChange}
                            slotProps={{ input: { min: 0 } }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>รวม</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {processData
                      .filter((proc) => proc.coreprocess)
                      .reduce(
                        (sum, proc) => sum + Number(formData[proc.key] || 0),
                        0
                      )}
                    %
                  </TableCell>
                </TableRow>
                {processData
                  .filter((proc) => !proc.coreprocess)
                  .map((proc) => {
                    let salaryKey = "";
                    if (proc.role.includes("โครงการ")) salaryKey = "pm_salary";
                    else if (
                      proc.role.includes("วิเคราะห์") ||
                      proc.role.includes("ออกแบบ")
                    )
                      salaryKey = "sa_salary";
                    else if (proc.role.includes("นักพัฒนาซอฟต์แวร์"))
                      salaryKey = "dev_salary";
                    else if (proc.role.includes("นักทดสอบ"))
                      salaryKey = "tester_salary";
                    else if (proc.role.includes("ฝ่ายปฏิบัติการ"))
                      salaryKey = "workteam_salary";
                    else salaryKey = "support_salary";

                    return (
                      <TableRow key={proc.key}>
                        <TableCell>{proc.process}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            name={proc.key}
                            value={formData[proc.key] || ""}
                            // value={formData[proc.key] || 0}
                            onChange={handleChange}
                            slotProps={{ input: { min: 0 } }}
                          />
                        </TableCell>
                        <TableCell>{parameters[proc.key] ?? "-"}</TableCell>
                        <TableCell>{proc.role}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            name={salaryKey}
                            value={formData[salaryKey] || ""}
                            // value={formData[salaryKey] || ""}
                            onChange={handleChange}
                            slotProps={{ input: { min: 0 } }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="space-between" >
            <Button variant="contained" color="success" onClick={handleSave} sx={{ fontWeight: "bold" }}>
              บันทึก
            </Button>
            <ButtonGroup>
              <Button variant="contained" color="inherit" onClick={handleBack} sx={{ fontWeight: "bold" }}>
                ย้อนกลับ
              </Button>
              <Button variant="contained" color="primary" onClick={handleNext} sx={{ fontWeight: "bold" }}>
                ถัดไป
              </Button>
            </ButtonGroup>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default EditSoftware;
